import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, Loader2, RefreshCw, Target,
  AlertTriangle, CheckCircle2, ArrowRight, DollarSign, BarChart3,
  Clock, Shield
} from 'lucide-react';

export default function RevenueForecast() {
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => { fetchForecast(); }, []);

  const fetchForecast = async () => {
    try {
      const res = await api.get('/api/revenue-forecast/');
      if (res.data.exists) setForecast(res.data.data);
    } catch (e) {
      toast.error('Failed to load forecast');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/api/revenue-forecast/generate');
      setForecast(res.data.data);
      toast.success('Revenue forecast generated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate forecast');
    } finally {
      setGenerating(false);
    }
  };

  const formatCurrency = (val) => {
    if (!val && val !== 0) return '$0';
    return `$${Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  };

  const riskColor = (level) => {
    switch (level) {
      case 'low': return 'text-emerald-600 bg-emerald-50 border-emerald-200';
      case 'medium': return 'text-amber-600 bg-amber-50 border-amber-200';
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-zinc-600 bg-zinc-50';
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4" data-testid="forecast-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-3 gap-4">
          {[1,2,3].map(i => <Skeleton key={i} className="h-40" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-6xl" data-testid="revenue-forecast-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Revenue Forecast</h1>
          <p className="text-sm text-muted-foreground mt-1">
            AI-powered probabilistic revenue forecast based on your pipeline data.
          </p>
        </div>
        <Button onClick={handleGenerate} disabled={generating} data-testid="generate-forecast-btn">
          {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          {forecast ? 'Refresh Forecast' : 'Generate Forecast'}
        </Button>
      </div>

      {!forecast ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-1">No forecast yet</h3>
            <p className="text-sm text-muted-foreground mb-4">Generate an AI-powered revenue forecast from your pipeline data.</p>
            <Button onClick={handleGenerate} disabled={generating} data-testid="generate-forecast-empty-btn">
              {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <BarChart3 className="h-4 w-4 mr-2" />}
              Generate Forecast (0.12 credits)
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Forecast Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="forecast-bands">
            {['30d', '60d', '90d'].map(period => {
              const f = forecast[`forecast_${period}`];
              if (!f) return null;
              return (
                <Card key={period} data-testid={`forecast-${period}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Clock className="h-4 w-4" /> {period === '30d' ? '30 Day' : period === '60d' ? '60 Day' : '90 Day'} Forecast
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">Best Case</span>
                        <span className="text-sm font-medium text-emerald-600">{formatCurrency(f.best_case)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-medium">Expected</span>
                        <span className="text-lg font-bold">{formatCurrency(f.expected)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">Worst Case</span>
                        <span className="text-sm font-medium text-red-600">{formatCurrency(f.worst_case)}</span>
                      </div>
                      <div className="pt-1">
                        <div className="flex items-center gap-1">
                          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-blue-500 rounded-full"
                              style={{ width: `${f.confidence || 0}%` }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground">{f.confidence}%</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Pipeline Health */}
          {forecast.pipeline_health && (
            <Card data-testid="pipeline-health-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Shield className="h-4 w-4" /> Pipeline Health
                  <Badge variant="outline" className="ml-2">
                    Score: {forecast.pipeline_health.score}/100
                  </Badge>
                  <Badge variant={
                    forecast.pipeline_health.trend === 'improving' ? 'default' :
                    forecast.pipeline_health.trend === 'declining' ? 'destructive' : 'secondary'
                  }>
                    {forecast.pipeline_health.trend === 'improving' && <TrendingUp className="h-3 w-3 mr-1" />}
                    {forecast.pipeline_health.trend === 'declining' && <TrendingDown className="h-3 w-3 mr-1" />}
                    {forecast.pipeline_health.trend}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {forecast.pipeline_health.key_risks?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium mb-2 flex items-center gap-1"><AlertTriangle className="h-3 w-3 text-amber-500" /> Key Risks</h4>
                      <ul className="space-y-1">
                        {forecast.pipeline_health.key_risks.map((r, i) => (
                          <li key={i} className="text-sm text-muted-foreground flex items-start gap-1">
                            <span className="text-amber-500 mt-1">-</span> {r}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {forecast.pipeline_health.opportunities?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium mb-2 flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-emerald-500" /> Opportunities</h4>
                      <ul className="space-y-1">
                        {forecast.pipeline_health.opportunities.map((o, i) => (
                          <li key={i} className="text-sm text-muted-foreground flex items-start gap-1">
                            <span className="text-emerald-500 mt-1">+</span> {o}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Deal Predictions */}
          {forecast.deal_predictions?.length > 0 && (
            <Card data-testid="deal-predictions">
              <CardHeader>
                <CardTitle className="text-base">Deal Predictions</CardTitle>
                <CardDescription>Win probability and risk assessment per deal</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {forecast.deal_predictions.map((deal, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-muted text-xs font-bold">
                          {deal.win_probability}%
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{deal.deal_name}</div>
                          <div className="text-xs text-muted-foreground">{formatCurrency(deal.value)}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`text-xs ${riskColor(deal.risk_level)}`}>
                          {deal.risk_level} risk
                        </Badge>
                        {deal.predicted_close_date && (
                          <span className="text-xs text-muted-foreground">{deal.predicted_close_date}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recommendations */}
          {forecast.recommendations?.length > 0 && (
            <Card data-testid="forecast-recommendations">
              <CardHeader>
                <CardTitle className="text-base">AI Recommendations</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {forecast.recommendations.map((rec, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <ArrowRight className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Summary & Snapshot */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {forecast.summary && (
              <Card data-testid="forecast-summary">
                <CardHeader><CardTitle className="text-base">Executive Summary</CardTitle></CardHeader>
                <CardContent><p className="text-sm text-muted-foreground">{forecast.summary}</p></CardContent>
              </Card>
            )}
            {forecast.pipeline_snapshot && (
              <Card data-testid="pipeline-snapshot">
                <CardHeader><CardTitle className="text-base">Pipeline Snapshot</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    <div><span className="text-xs text-muted-foreground">Active Deals</span><div className="font-bold">{forecast.pipeline_snapshot.total_deals}</div></div>
                    <div><span className="text-xs text-muted-foreground">Pipeline Value</span><div className="font-bold">{formatCurrency(forecast.pipeline_snapshot.total_pipeline_value)}</div></div>
                    <div><span className="text-xs text-muted-foreground">Win Rate</span><div className="font-bold">{forecast.pipeline_snapshot.historical_win_rate}%</div></div>
                    <div><span className="text-xs text-muted-foreground">Avg Deal</span><div className="font-bold">{formatCurrency(forecast.pipeline_snapshot.avg_deal_size)}</div></div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <p className="text-xs text-muted-foreground text-right">
            Generated: {forecast.generated_at ? new Date(forecast.generated_at).toLocaleString() : 'N/A'}
          </p>
        </>
      )}
    </div>
  );
}
